/**
 * The ways the shell declines to render a route's content.
 *
 * They are separate components because they are separate facts, the same way
 * `ApiFailure` keeps 401 and 403 apart: "you do not hold this grant" is a
 * settled answer the caller can act on, "you are not signed in" is one they can
 * fix in the next click, and "we could not find out" is neither. Folding them into a single "not allowed" screen would tell a tenant
 * whose network dropped that their access had been revoked.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { useLocation, useNavigate } from '@tanstack/react-router';

import type { ApiFailureError } from '../../lib/http/errors';
import { t } from '../../i18n';
import { AUTH_ROUTE_PATHS, HOME_PATH } from '../routes/paths';
import type { SessionState } from '../session/authState';
import { ts } from './messages';

export function Forbidden(): ReactNode {
  return (
    <section role="alert" className="mx-auto max-w-prose p-8">
      <h1 className="text-xl font-medium tracking-tight">{ts('forbidden.title')}</h1>
      <p className="text-muted-foreground mt-2 text-sm">{ts('forbidden.body')}</p>
    </section>
  );
}

/**
 * Rendered when `/control/me` did not answer. Deliberately renders no route
 * content: an entitlement that could not be read is not an entitlement held.
 */
export function SessionUnavailable(props: { onRetry?: () => void }): ReactNode {
  return (
    <section role="alert" className="mx-auto max-w-prose p-8">
      <h1 className="text-xl font-medium tracking-tight">{ts('session.error.title')}</h1>
      <p className="text-muted-foreground mt-2 text-sm">{ts('session.error.body')}</p>
      {props.onRetry ? (
        <button type="button" className="mt-4 text-sm underline" onClick={props.onRetry}>
          {t('common.retry')}
        </button>
      ) : null}
    </section>
  );
}

/**
 * `/control/me` answered 401. Not a page: the only thing the caller can do is
 * sign in, so they are sent to the form rather than shown a link to it.
 */
export function SignInRequired(): ReactNode {
  // Reached mid-journey as often as on first load -- a session dropping while
  // someone is standing on a page they were using, not only at the front
  // door -- so the redirect carries the page they were on and `sign-in.tsx`
  // returns them there instead of always to `/`.
  //
  // Read once: the shell stays mounted while the navigation is pending, and by
  // then `useLocation` answers with the sign-in URL itself, which would nest a
  // redirect inside a redirect on every render.
  const [redirect] = useState(useLocation().href);
  const navigate = useNavigate();
  useEffect(() => {
    // Signing out from `/` is the common case and `/` is where sign-in
    // lands anyway, so carrying it would spend a search param on the URL a
    // stranger sees to say what the form already does.
    const search = redirect === HOME_PATH ? undefined : { redirect };
    void navigate({ to: AUTH_ROUTE_PATHS.signIn, search, replace: true });
  }, [navigate, redirect]);
  return null;
}

/**
 * What every signed-in page defers to before its own content: Firebase
 * hasn't answered yet, `/control/me` refused authentication, or it failed
 * for any other reason. `null` means proceed -- render the caller's own
 * content. Shared between `AppShell` and `Home` (`../routes/root.tsx`)
 * because `Home` renders outside the shell and would otherwise face a
 * signed-out or errored caller with nothing on screen at all.
 */
export function sessionGate(
  session: SessionState,
  error: ApiFailureError | null,
  onRetry: () => void,
): ReactNode | null {
  if (session.status === 'loading') {
    return (
      <p role="status" className="text-muted-foreground p-8 text-sm">
        {t('shell.loading')}
      </p>
    );
  }
  if (error?.failure.kind === 'unauthenticated') return <SignInRequired />;
  if (error) return <SessionUnavailable onRetry={onRetry} />;
  return null;
}
